import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import './NewVenta.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPlus, faTimes, faCheck, faUser, faBox, faDollarSign,
    faInfoCircle, faTag, faPenToSquare, faBoxesStacked, faHandHoldingDollar,
    faSearch, faChevronDown, faImage
} from '@fortawesome/free-solid-svg-icons';
import ventasService from '../../../services/ventasService';
import moneda from '../../moneda';

// ============= Toast =============
const Toast = ({ toasts }) => {
    if (toasts.length === 0) return null;
    return (
        <div className="product-toast-container">
            {toasts.map(t => (
                <div key={t.id} className={`product-toast ${t.type}`}>
                    <div className="toast-icon">
                        {t.type === 'success' && '✅'}
                        {t.type === 'error' && '❌'}
                        {t.type === 'warning' && '⚠️'}
                        {t.type === 'info' && 'ℹ️'}
                    </div>
                    <div className="toast-content">
                        <div className="toast-title">{t.message}</div>
                    </div>
                </div>
            ))}
        </div>
    );
};

export default function NewVenta({ onCreated }) {
    const [modalOpen, setModalOpen] = useState(false);
    const [enviando, setEnviando] = useState(false);
    const [toasts, setToasts] = useState([]);

    // catálogo
    const [productos, setProductos] = useState([]);

    // cliente
    const [clienteNombre, setClienteNombre] = useState('');
    const [clienteTelefono, setClienteTelefono] = useState('');
    const [clienteIdentificacion, setClienteIdentificacion] = useState('');

    // elemento
    const [modoElemento, setModoElemento] = useState('catalogo'); // 'catalogo' | 'libre'
    const [idProducto, setIdProducto] = useState('');
    const [elementoNombre, setElementoNombre] = useState('');
    const [elementoImagen, setElementoImagen] = useState('');

    // dropdown de catálogo con buscador
    const [dropdownAbierto, setDropdownAbierto] = useState(false);
    const [busqueda, setBusqueda] = useState('');
    const [comboCoords, setComboCoords] = useState({ top: 0, left: 0, width: 0 });
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    // clientes (reutilización + historial)
    const [clientes, setClientes] = useState([]);
    const [clienteComboAbierto, setClienteComboAbierto] = useState(false);
    const [clienteBusqueda, setClienteBusqueda] = useState('');
    const [clienteCoords, setClienteCoords] = useState({ top: 0, left: 0, width: 0 });
    const [clienteConocido, setClienteConocido] = useState(null);
    const clienteDropdownRef = useRef(null);
    const clienteTriggerRef = useRef(null);
    const clientePanelRef = useRef(null);
    // historial del cliente seleccionado
    const [historialAbierto, setHistorialAbierto] = useState(false);
    const [historial, setHistorial] = useState(null);
    const [historialLoading, setHistorialLoading] = useState(false);

    // valores
    const [valorCompra, setValorCompra] = useState('');
    const [valorVenta, setValorVenta] = useState('');

    // pago
    const [abonoInicial, setAbonoInicial] = useState('');
    const [nota, setNota] = useState('');

    const showToast = (message, type = 'info') => {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev, { id, message, type }]);
        setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
    };

    useEffect(() => {
        if (modalOpen) {
            if (productos.length === 0) ventasService.listarProductos().then(setProductos);
            ventasService.listarClientes('').then(setClientes);
        }
    }, [modalOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // ---- Combo de clientes (portal + coords, mismo patrón que el catálogo) ----
    const actualizarCoordsCliente = () => {
        if (!clienteTriggerRef.current) return;
        const r = clienteTriggerRef.current.getBoundingClientRect();
        setClienteCoords({ top: r.bottom + 6, left: r.left, width: r.width });
    };

    useEffect(() => {
        if (!clienteComboAbierto) return;
        actualizarCoordsCliente();
        window.addEventListener('resize', actualizarCoordsCliente);
        window.addEventListener('scroll', actualizarCoordsCliente, true);
        return () => {
            window.removeEventListener('resize', actualizarCoordsCliente);
            window.removeEventListener('scroll', actualizarCoordsCliente, true);
        };
    }, [clienteComboAbierto]);

    useEffect(() => {
        if (!clienteComboAbierto) return;
        const handleClickFuera = (e) => {
            const dentroTrigger = clienteDropdownRef.current?.contains(e.target);
            const dentroPanel = clientePanelRef.current?.contains(e.target);
            if (!dentroTrigger && !dentroPanel) setClienteComboAbierto(false);
        };
        document.addEventListener('mousedown', handleClickFuera);
        return () => document.removeEventListener('mousedown', handleClickFuera);
    }, [clienteComboAbierto]);

    const clientesFiltrados = useMemo(() => {
        const q = clienteBusqueda.trim().toLowerCase();
        if (!q) return clientes;
        return clientes.filter(c =>
            (c.clienteNombre || '').toLowerCase().includes(q) ||
            (c.clienteTelefono || '').toLowerCase().includes(q) ||
            (c.clienteIdentificacion || '').toLowerCase().includes(q)
        );
    }, [clientes, clienteBusqueda]);

    const claveCliente = (c) =>
        (c?.clienteIdentificacion || '').trim() || `${c?.clienteNombre || ''}|${c?.clienteTelefono || ''}`;

    const onSeleccionarCliente = (c) => {
        setClienteNombre(c.clienteNombre || '');
        setClienteTelefono(c.clienteTelefono || '');
        setClienteIdentificacion(c.clienteIdentificacion || '');
        setClienteConocido(c);
        setClienteComboAbierto(false);
        setClienteBusqueda('');
        setHistorialAbierto(false);
        setHistorial(null);
    };

    // Si el usuario edita a mano, deja de considerarse "cliente conocido"
    const onEditarClienteManual = (setter) => (e) => {
        setter(e.target.value);
        if (clienteConocido) {
            setClienteConocido(null);
            setHistorialAbierto(false);
            setHistorial(null);
        }
    };

    const toggleHistorial = async () => {
        if (!clienteConocido) return;
        const abrir = !historialAbierto;
        setHistorialAbierto(abrir);
        if (abrir && !historial) {
            setHistorialLoading(true);
            const ident = (clienteConocido.clienteIdentificacion || '').trim();
            const ref = ident
                ? { identificacion: ident }
                : { nombre: clienteConocido.clienteNombre, telefono: clienteConocido.clienteTelefono || '' };
            const data = await ventasService.historialCliente(ref);
            setHistorialLoading(false);
            if (data.error) showToast(data.error, 'error');
            else setHistorial(data);
        }
    };

    // Recalcular la posición del panel (renderizado en un portal) según el trigger
    const actualizarCoords = () => {
        if (!triggerRef.current) return;
        const r = triggerRef.current.getBoundingClientRect();
        setComboCoords({ top: r.bottom + 6, left: r.left, width: r.width });
    };

    // Al abrir: posicionar el panel y mantenerlo alineado ante scroll/resize
    useEffect(() => {
        if (!dropdownAbierto) return;
        actualizarCoords();
        window.addEventListener('resize', actualizarCoords);
        window.addEventListener('scroll', actualizarCoords, true); // capture: incluye scroll del modal
        return () => {
            window.removeEventListener('resize', actualizarCoords);
            window.removeEventListener('scroll', actualizarCoords, true);
        };
    }, [dropdownAbierto]);

    // Cerrar el dropdown al hacer clic fuera (trigger + panel del portal)
    useEffect(() => {
        if (!dropdownAbierto) return;
        const handleClickFuera = (e) => {
            const dentroTrigger = dropdownRef.current?.contains(e.target);
            const dentroPanel = panelRef.current?.contains(e.target);
            if (!dentroTrigger && !dentroPanel) {
                setDropdownAbierto(false);
            }
        };
        document.addEventListener('mousedown', handleClickFuera);
        return () => document.removeEventListener('mousedown', handleClickFuera);
    }, [dropdownAbierto]);

    // Imagen de un producto del catálogo (miniatura / preview)
    const imagenDe = (p) => p?.imagenPrincipal || p?.imagen1 || '';

    // Producto actualmente seleccionado
    const productoSeleccionado = useMemo(
        () => productos.find(pr => String(pr.idProducto) === String(idProducto)) || null,
        [productos, idProducto]
    );

    // Catálogo filtrado por el buscador
    const productosFiltrados = useMemo(() => {
        const q = busqueda.trim().toLowerCase();
        if (!q) return productos;
        return productos.filter(p => (p.titulo || '').toLowerCase().includes(q));
    }, [productos, busqueda]);

    // Al elegir un producto del catálogo, autocompletar datos (editables)
    const onSeleccionarProducto = (id) => {
        setIdProducto(id);
        const p = productos.find(pr => String(pr.idProducto) === String(id));
        if (p) {
            setElementoNombre(p.titulo || '');
            setElementoImagen(imagenDe(p));
            if (p.costo !== undefined && p.costo !== null && p.costo !== '') {
                setValorCompra(String(p.costo));
            }
            if (p.precio !== undefined && p.precio !== null && p.precio !== '') {
                setValorVenta(String(p.precio));
            }
        }
        setDropdownAbierto(false);
        setBusqueda('');
    };

    const ganancia = useMemo(() => {
        const c = parseFloat(valorCompra);
        const v = parseFloat(valorVenta);
        if (Number.isNaN(c) || Number.isNaN(v)) return null;
        return v - c;
    }, [valorCompra, valorVenta]);

    const margen = useMemo(() => {
        const v = parseFloat(valorVenta);
        if (ganancia === null || !v) return null;
        return (ganancia / v) * 100;
    }, [ganancia, valorVenta]);

    const formatMoney = (n) =>
        `${moneda} ${Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const toggleModal = () => {
        if (modalOpen) resetForm();
        setModalOpen(!modalOpen);
    };

    const resetForm = () => {
        setClienteNombre('');
        setClienteTelefono('');
        setClienteIdentificacion('');
        setClienteConocido(null);
        setClienteComboAbierto(false);
        setClienteBusqueda('');
        setHistorialAbierto(false);
        setHistorial(null);
        setModoElemento('catalogo');
        setIdProducto('');
        setElementoNombre('');
        setElementoImagen('');
        setDropdownAbierto(false);
        setBusqueda('');
        setValorCompra('');
        setValorVenta('');
        setAbonoInicial('');
        setNota('');
    };

    const cambiarModo = (modo) => {
        setModoElemento(modo);
        // limpiar campos del elemento al cambiar de modo
        setIdProducto('');
        setElementoNombre('');
        setElementoImagen('');
        setDropdownAbierto(false);
        setBusqueda('');
    };

    const validar = () => {
        if (!clienteNombre.trim()) return showToast('El nombre del cliente es obligatorio', 'error'), false;
        if (!elementoNombre.trim()) return showToast('Indique el elemento vendido', 'error'), false;
        const c = parseFloat(valorCompra);
        const v = parseFloat(valorVenta);
        if (Number.isNaN(c) || c < 0) return showToast('Ingrese un valor de compra válido', 'error'), false;
        if (Number.isNaN(v) || v <= 0) return showToast('Ingrese un valor de venta válido', 'error'), false;
        if (v < c) return showToast('El valor de venta no puede ser menor al de compra', 'error'), false;
        if (abonoInicial !== '') {
            const a = parseFloat(abonoInicial);
            if (Number.isNaN(a) || a < 0) return showToast('El abono inicial no es válido', 'error'), false;
            if (a > v) return showToast('El abono inicial no puede superar el valor de venta', 'error'), false;
        }
        return true;
    };

    const registrarVenta = async () => {
        if (!validar()) return;
        setEnviando(true);
        const payload = {
            clienteNombre: clienteNombre.trim(),
            clienteTelefono: clienteTelefono.trim(),
            clienteIdentificacion: clienteIdentificacion.trim(),
            idProducto: modoElemento === 'catalogo' && idProducto ? parseInt(idProducto) : null,
            elementoNombre: elementoNombre.trim(),
            elementoImagen: elementoImagen || '',
            valorCompra: parseFloat(valorCompra),
            valorVenta: parseFloat(valorVenta),
            abonoInicial: abonoInicial !== '' ? parseFloat(abonoInicial) : 0,
            nota: nota.trim(),
        };
        const result = await ventasService.crear(payload);
        setEnviando(false);
        if (result.error) {
            showToast(result.error, 'error');
            return;
        }
        showToast(result.mensaje || 'Venta registrada', 'success');
        toggleModal();
        if (typeof onCreated === 'function') onCreated();
    };

    return (
        <div className='NewContain'>
            <Toast toasts={toasts} />

            <button onClick={toggleModal} className='btn-product-primary flex-center btn-nueva-venta' style={{ gap: 8 }}>
                <FontAwesomeIcon icon={faPlus} size="sm" /> Nueva Venta
            </button>

            {modalOpen && (
                <div className='product-modal-backdrop'>
                    <div className='product-modal-container' style={{ maxWidth: 820 }}>
                        {/* Header */}
                        <div className='product-modal-header'>
                            <div className='product-modal-title'>
                                <FontAwesomeIcon icon={faHandHoldingDollar} size="lg" /> Registrar Nueva Venta
                            </div>
                            <button className='product-close-button' onClick={toggleModal}>
                                <FontAwesomeIcon icon={faTimes} size="lg" />
                            </button>
                        </div>

                        {/* Body */}
                        <div className='product-modal-body'>
                            {/* Cliente */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faUser} /> Datos del Cliente</div>
                                </div>
                                <div className="product-section-content">
                                    {/* Buscador de cliente existente (reutilizar datos) */}
                                    <div className='product-form-group' style={{ marginBottom: 16 }}>
                                        <label className='product-form-label'><FontAwesomeIcon icon={faSearch} size="sm" /> Buscar cliente existente</label>
                                        <div className="venta-combo" ref={clienteDropdownRef}>
                                            <button
                                                ref={clienteTriggerRef}
                                                type="button"
                                                className={`venta-combo-trigger ${clienteComboAbierto ? 'open' : ''}`}
                                                onClick={() => setClienteComboAbierto(o => !o)}
                                            >
                                                <span className="venta-combo-placeholder">
                                                    {clienteConocido ? `${clienteConocido.clienteNombre}` : 'Buscar por nombre, cédula o teléfono...'}
                                                </span>
                                                <FontAwesomeIcon icon={faChevronDown} className="venta-combo-caret" />
                                            </button>

                                            {clienteComboAbierto && createPortal(
                                                <div
                                                    ref={clientePanelRef}
                                                    className="venta-combo-panel"
                                                    style={{ top: clienteCoords.top, left: clienteCoords.left, width: clienteCoords.width }}
                                                >
                                                    <div className="venta-combo-search">
                                                        <FontAwesomeIcon icon={faSearch} />
                                                        <input
                                                            autoFocus
                                                            type="text"
                                                            value={clienteBusqueda}
                                                            onChange={e => setClienteBusqueda(e.target.value)}
                                                            placeholder="Buscar cliente..."
                                                        />
                                                    </div>
                                                    <ul className="venta-combo-list">
                                                        {clientesFiltrados.length === 0 ? (
                                                            <li className="venta-combo-empty">Sin clientes previos</li>
                                                        ) : (
                                                            clientesFiltrados.map(c => (
                                                                <li key={claveCliente(c)}>
                                                                    <button
                                                                        type="button"
                                                                        className="venta-combo-item"
                                                                        onClick={() => onSeleccionarCliente(c)}
                                                                    >
                                                                        <span className="cliente-avatar"><FontAwesomeIcon icon={faUser} /></span>
                                                                        <span className="venta-combo-item-title">
                                                                            {c.clienteNombre}
                                                                            <small className="text-muted" style={{ display: 'block' }}>
                                                                                {c.clienteIdentificacion ? `CC ${c.clienteIdentificacion}` : (c.clienteTelefono || 'Sin contacto')} · {c.total_ventas} compra(s)
                                                                            </small>
                                                                        </span>
                                                                        {c.saldo_pendiente > 0 && (
                                                                            <span className="venta-combo-item-price" style={{ color: '#dc2626' }}>
                                                                                Debe {formatMoney(c.saldo_pendiente)}
                                                                            </span>
                                                                        )}
                                                                    </button>
                                                                </li>
                                                            ))
                                                        )}
                                                    </ul>
                                                </div>,
                                                document.body
                                            )}
                                        </div>
                                        <small className="text-muted">Al elegir un cliente se autocompletan sus datos (puedes editarlos). Si es nuevo, solo escribe abajo.</small>
                                    </div>

                                    {/* Resumen del cliente conocido + acceso a historial */}
                                    {clienteConocido && (
                                        <div className="cliente-resumen">
                                            <div className="cliente-resumen-info">
                                                <FontAwesomeIcon icon={faUser} />
                                                <span>
                                                    <strong>{clienteConocido.clienteNombre}</strong> · {clienteConocido.total_ventas} compra(s)
                                                    {clienteConocido.saldo_pendiente > 0
                                                        ? <> · <span style={{ color: '#dc2626', fontWeight: 600 }}>saldo {formatMoney(clienteConocido.saldo_pendiente)}</span></>
                                                        : <> · <span style={{ color: '#16a34a', fontWeight: 600 }}>al día</span></>}
                                                </span>
                                            </div>
                                            <button type="button" className="cliente-historial-btn" onClick={toggleHistorial}>
                                                <FontAwesomeIcon icon={faInfoCircle} /> {historialAbierto ? 'Ocultar historial' : 'Ver historial'}
                                            </button>
                                        </div>
                                    )}

                                    {clienteConocido && historialAbierto && (
                                        <div className="cliente-historial">
                                            {historialLoading ? (
                                                <div className="text-muted" style={{ padding: 12 }}>Cargando historial...</div>
                                            ) : historial && historial.ventas && historial.ventas.length > 0 ? (
                                                <>
                                                    <div className="cliente-historial-head">
                                                        <span>{historial.resumen.total_ventas} venta(s) · comprado {formatMoney(historial.resumen.total_comprado)} · saldo {formatMoney(historial.resumen.saldo_pendiente)}</span>
                                                    </div>
                                                    <ul className="cliente-historial-list">
                                                        {historial.ventas.map(v => (
                                                            <li key={v.idVenta}>
                                                                <span className="cliente-historial-el">{v.elementoNombre}</span>
                                                                <span className={`cliente-historial-estado estado-${(v.estado || '').toLowerCase()}`}>{v.estado}</span>
                                                                <span className="cliente-historial-monto">{formatMoney(v.valorVenta)}</span>
                                                                <span className="cliente-historial-fecha text-muted">{(v.createdAt || '').slice(0, 10)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </>
                                            ) : (
                                                <div className="text-muted" style={{ padding: 12 }}>Sin compras registradas.</div>
                                            )}
                                        </div>
                                    )}

                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'><FontAwesomeIcon icon={faUser} size="sm" /> Nombre *</label>
                                            <input className='product-form-input' value={clienteNombre} onChange={onEditarClienteManual(setClienteNombre)} maxLength={120} />
                                        </div>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'>Teléfono / Contacto</label>
                                            <input className='product-form-input' value={clienteTelefono} onChange={onEditarClienteManual(setClienteTelefono)} maxLength={40} />
                                        </div>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'>Identificación</label>
                                            <input className='product-form-input' value={clienteIdentificacion} onChange={onEditarClienteManual(setClienteIdentificacion)} maxLength={60} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Elemento */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faBox} /> Elemento Vendido</div>
                                </div>
                                <div className="product-section-content">
                                    {/* Selector de modo */}
                                    <div className="venta-modo-toggle">
                                        <button
                                            type="button"
                                            className={`venta-modo-btn ${modoElemento === 'catalogo' ? 'active' : ''}`}
                                            onClick={() => cambiarModo('catalogo')}
                                        >
                                            <FontAwesomeIcon icon={faBoxesStacked} /> Del catálogo
                                        </button>
                                        <button
                                            type="button"
                                            className={`venta-modo-btn ${modoElemento === 'libre' ? 'active' : ''}`}
                                            onClick={() => cambiarModo('libre')}
                                        >
                                            <FontAwesomeIcon icon={faPenToSquare} /> Entrada libre
                                        </button>
                                    </div>

                                    {modoElemento === 'catalogo' ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginTop: 16 }}>
                                            <div className='product-form-group'>
                                                <label className='product-form-label'><FontAwesomeIcon icon={faTag} size="sm" /> Producto del catálogo</label>
                                                <div className="venta-combo" ref={dropdownRef}>
                                                    <button
                                                        ref={triggerRef}
                                                        type="button"
                                                        className={`venta-combo-trigger ${dropdownAbierto ? 'open' : ''}`}
                                                        onClick={() => setDropdownAbierto(o => !o)}
                                                    >
                                                        {productoSeleccionado ? (
                                                            <span className="venta-combo-selected">
                                                                {imagenDe(productoSeleccionado) ? (
                                                                    <img src={imagenDe(productoSeleccionado)} alt={productoSeleccionado.titulo} />
                                                                ) : (
                                                                    <span className="venta-combo-thumb-fallback"><FontAwesomeIcon icon={faImage} /></span>
                                                                )}
                                                                <span className="venta-combo-selected-text">{productoSeleccionado.titulo}</span>
                                                            </span>
                                                        ) : (
                                                            <span className="venta-combo-placeholder">Seleccionar producto...</span>
                                                        )}
                                                        <FontAwesomeIcon icon={faChevronDown} className="venta-combo-caret" />
                                                    </button>

                                                    {dropdownAbierto && createPortal(
                                                        <div
                                                            ref={panelRef}
                                                            className="venta-combo-panel"
                                                            style={{ top: comboCoords.top, left: comboCoords.left, width: comboCoords.width }}
                                                        >
                                                            <div className="venta-combo-search">
                                                                <FontAwesomeIcon icon={faSearch} />
                                                                <input
                                                                    autoFocus
                                                                    type="text"
                                                                    value={busqueda}
                                                                    onChange={e => setBusqueda(e.target.value)}
                                                                    placeholder="Buscar producto..."
                                                                />
                                                            </div>
                                                            <ul className="venta-combo-list">
                                                                {productosFiltrados.length === 0 ? (
                                                                    <li className="venta-combo-empty">Sin resultados</li>
                                                                ) : (
                                                                    productosFiltrados.map(p => (
                                                                        <li key={p.idProducto}>
                                                                            <button
                                                                                type="button"
                                                                                className={`venta-combo-item ${String(p.idProducto) === String(idProducto) ? 'active' : ''}`}
                                                                                onClick={() => onSeleccionarProducto(p.idProducto)}
                                                                            >
                                                                                {imagenDe(p) ? (
                                                                                    <img src={imagenDe(p)} alt={p.titulo} />
                                                                                ) : (
                                                                                    <span className="venta-combo-thumb-fallback"><FontAwesomeIcon icon={faImage} /></span>
                                                                                )}
                                                                                <span className="venta-combo-item-title">{p.titulo}</span>
                                                                                {(p.precio !== undefined && p.precio !== null && p.precio !== '') && (
                                                                                    <span className="venta-combo-item-price">{formatMoney(p.precio)}</span>
                                                                                )}
                                                                            </button>
                                                                        </li>
                                                                    ))
                                                                )}
                                                            </ul>
                                                        </div>,
                                                        document.body
                                                    )}
                                                </div>
                                                <small className="text-muted">Al elegirlo se autocompletan nombre, compra y venta (puedes ajustarlos).</small>
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ marginTop: 16 }}>
                                            <div className='product-form-group'>
                                                <label className='product-form-label'><FontAwesomeIcon icon={faTag} size="sm" /> Nombre del elemento *</label>
                                                <input className='product-form-input' value={elementoNombre} onChange={e => setElementoNombre(e.target.value)} maxLength={160} placeholder="Ej: Vestido rojo talla M" />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Valores */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faDollarSign} /> Valores</div>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'>💰 Valor de compra (adquisición) *</label>
                                            <input type='number' className='product-form-input' value={valorCompra} onChange={e => setValorCompra(e.target.value)} min="0" step="0.01" />
                                        </div>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'><FontAwesomeIcon icon={faDollarSign} size="sm" /> Valor a pagar (venta) *</label>
                                            <input type='number' className='product-form-input' value={valorVenta} onChange={e => setValorVenta(e.target.value)} min="0" step="0.01" />
                                        </div>
                                    </div>

                                    {ganancia !== null && (
                                        <div style={{ marginTop: 16, padding: 12, background: ganancia >= 0 ? 'rgba(16,185,129,.1)' : 'rgba(239,68,68,.1)', borderRadius: 8, border: `1px solid ${ganancia >= 0 ? '#10b981' : '#ef4444'}` }}>
                                            <div style={{ fontWeight: 600, marginBottom: 4 }}>Análisis de Rentabilidad:</div>
                                            <div style={{ color: ganancia >= 0 ? '#16a34a' : '#dc2626', fontSize: 14 }}>
                                                {ganancia >= 0
                                                    ? (<><strong>Ganancia: {formatMoney(ganancia)}</strong>{margen !== null && <> · Margen: {margen.toFixed(1)}%</>}</>)
                                                    : (<strong>⚠️ El valor de venta es menor al de compra</strong>)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Pago / seguimiento */}
                            <div className="product-section-card mb-24">
                                <div className="product-section-header">
                                    <div className="product-section-title"><FontAwesomeIcon icon={faHandHoldingDollar} /> Pago (opcional)</div>
                                </div>
                                <div className="product-section-content">
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                                        <div className='product-form-group'>
                                            <label className='product-form-label'>Abono inicial</label>
                                            <input type='number' className='product-form-input' value={abonoInicial} onChange={e => setAbonoInicial(e.target.value)} min="0" step="0.01" placeholder="0" />
                                        </div>
                                        <div className='product-form-group' style={{ gridColumn: '1 / -1' }}>
                                            <label className='product-form-label'>Nota</label>
                                            <textarea className='product-form-input' value={nota} onChange={e => setNota(e.target.value)} maxLength={300} rows={2} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className='product-submit-section'>
                            <button type='button' onClick={toggleModal} className='btn-product-secondary'>Cancelar</button>
                            {enviando ? (
                                <button type='button' className='btn-product-primary' disabled style={{ opacity: .6 }}>
                                    <FontAwesomeIcon icon={faInfoCircle} style={{ marginRight: 8 }} /> Procesando...
                                </button>
                            ) : (
                                <button
                                    type='button'
                                    onClick={registrarVenta}
                                    className='btn-product-primary flex-center'
                                    style={{ gap: 8 }}
                                    disabled={!clienteNombre.trim() || !elementoNombre.trim() || !valorCompra || !valorVenta}
                                >
                                    <FontAwesomeIcon icon={faCheck} size="sm" /> Registrar Venta
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
